import { Box, Button, IconButton, Tab, Tabs } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import SearchBar from "components/SearchBar";
import TableComponent from "components/TableComponent";
import { showError, showToast } from "../../helpers/showToast";
import dayjs from "dayjs";
import MainContainer from "layout/MainContainer";
import { ConfirmationModal } from "Modals/confirmationModal";
import ResonOfRejection from "Modals/reasonOfRejection";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useLazyDeletePartnerQuery,
  useLazyGetAllPartnersQuery,
  usePartnerStatusMutation,
} from "service/partner";
import { CommonBody, Listing } from "types/General";
import { generateEncryptedKeyBody } from "utils/crypto";
import { isValidInput } from "utils/validations";
import CustomPagination from "components/Panigation";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManagePartners = () => {
  const navigate = useNavigate();
  const [getPartners] = useLazyGetAllPartnersQuery();
  const [changeStatus] = usePartnerStatusMutation();
  const [deletePartner] = useLazyDeletePartnerQuery();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [rejectionOpen, setRejectionOpen] = useState<boolean>(false);
  const [rejectionRsn, setRejectionRsn] = useState<string>("");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [valueTabs, setValueTabs] = React.useState(0);
  const [partners, setPartners] = useState([]);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
    setSearchTerm("");
  };

  const columns: GridColDef[] = [
    {
      flex: 0.8,
      minWidth: 200,
      field: "user",
      headerName: "User Details",
      editable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const { row } = params;
        return (
          <div className="user_block">
            <p>
              <b>{row?.partnerName || "-"}</b>
              {"+971"} {row?.partnerNumber || "-"} <br />
              {row?.partnerEmail || "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 0.8,
      minWidth: 200,
      field: "license",
      headerName: "License",
      editable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const { row } = params;
        return (
          <div className="user_block">
            <p>{row?.licenseNumber || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 0.6,
      minWidth: 200,
      field: "date",
      headerName: "Date & Time",
      editable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const { row } = params;
        return (
          <div className="user_block">
            <p>{dayjs(row?.createdAt).format("D MMM, YYYY h:mma")}</p>
          </div>
        );
      },
    },
    // Conditionally add the 'request' column only for Pending tab
    ...(valueTabs === 0
      ? [
          {
            minWidth: 200,
            field: "request",
            headerName: "Request",
            editable: false,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
              const { row } = params;
              return (
                <div className="user_block">
                  <Button
                    onClick={() => {
                      setSelectedId(row?._id);
                      setRejectionOpen(true);
                    }}
                    variant="outlined"
                    sx={{
                      color: "red",
                      borderColor: "red",
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.1)",
                        borderColor: "darkred",
                        color: "darkred",
                      },
                    }}
                    size="small"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleCategoryStatus(row?._id, 1)}
                    variant="outlined"
                    sx={{
                      color: "green",
                      borderColor: "green",
                      "&:hover": {
                        backgroundColor: "rgba(0, 128, 0, 0.1)", // light green background on hover
                        borderColor: "darkgreen",
                        color: "darkgreen",
                      },
                    }}
                    size="small"
                  >
                    Approve
                  </Button>
                </div>
              );
            },
          },
        ]
      : []),
    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="table_actions v2">
          <Box>
            <IconButton
              onClick={() => navigate(`/manage-partners/details/${row?._id}`)}
            >
              <img src="/static/images/eye_icon.svg" alt="" />
            </IconButton>
          </Box>

          <Box>
            <IconButton
              onClick={() => {
                setSelectedId(row?._id);
                setOpen(true);
                setModalType("delete");
              }}
            >
              <img src="/static/images/trash2_icon.svg" alt="" />
            </IconButton>
          </Box>
        </div>
      ),
    },
  ];

  const handleCategoryStatus = async (id: string, status: number) => {
    try {
      const body = {
        status: status,
        ...(status === 2 ? { rejectedReason: rejectionRsn } : {}),
        respondedAt: new Date().toISOString(),
        ...(status === 1 ? { permissions: [1] } : {}),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      const response = await changeStatus({
        id: id,
        body: encryptedBody,
      }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "");
        getPartnersApi();
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      console.error(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const getPartnersApi = async () => {
    setLoading(true);
    try {
      const response = await getPartners({
        status: valueTabs,
        page: page,
        search: debouncedSearchTerm,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setPartners(response?.data?.list);
        setCount(response?.data?.totalCount);
        setPage(response?.data?.currentPage);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const deleteClassApi = async (id: string) => {
    setLoading(true);
    try {
      const response = await deletePartner({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        getPartnersApi();
        showToast(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    getPartnersApi();
  }, [page, debouncedSearchTerm, valueTabs]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Partners</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Partners
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              {/* <Box>
                <Button className="btn btn_highlight">
                  <img src="/static/images/upload_icon.svg" alt="Icon" /> Export
                  CSV
                </Button>
              </Box> */}
            </div>
          </div>

          <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="basic tabs example"
            className="custom_tabs_links"
          >
            <Tab label="Pending" {...a11yProps(0)} />
            <Tab label="Accepted" {...a11yProps(1)} />
            <Tab label="Rejected" {...a11yProps(2)} />
          </Tabs>

          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={partners}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />

            {partners?.length ? (
              <CustomPagination
                currentPage={page - 1}
                totalPages={Math.ceil(count / 10)}
                count={count}
                pageSize={10}
                onPageChange={(page) => setPage(page + 1)}
              />
            ) : null}
          </div>
        </Box>

        <ResonOfRejection
          selectedId={selectedId}
          rejectionRsn={rejectionRsn}
          setRejectionRsn={setRejectionRsn}
          open={rejectionOpen}
          setOpen={setRejectionOpen}
          onClose={() => handleCategoryStatus(selectedId, 2)}
        />

        <ConfirmationModal
          text={
            modalType === "delete"
              ? "Are you sure to delete this listing?"
              : modalType === "accept"
                ? "Are you sure to approve this listing?"
                : modalType === "reject"
                  ? "Are you sure to reject this listing?"
                  : ""
          }
          openModal={open}
          setOpenModal={setOpen}
          handleSubmit={() => deleteClassApi(selectedId)}
        />
      </div>
    </MainContainer>
  );
};

export default ManagePartners;
